import React from 'react';

import { EXCLAMATION_CIRCLE } from '../icons';

/**
 * What the builder has to say about one control, under it: a server refusal
 * or a Save that could not go (`error`, the control is marked too), what the
 * question still lacks (`hint`), or what the chart will say about itself
 * once drawn (`note` — the preview's warnings, `no_door`, `per_currency`…).
 * Each is one sentence; a refusal is the server's own words.
 *
 *   messages  [{ tone: 'error' | 'hint' | 'note', text }]
 *   id        the id a control's aria-describedby points at
 */
export default function FieldMessage({ messages = [], id }) {
	const list = (Array.isArray(messages) ? messages : []).filter((m) => m && m.text);
	if (!list.length) return null;
	return (
		<div className='chart-builder__messages' id={id}>
			{list.map((m, i) => (
				<div
					key={i}
					className={`chart-builder__message chart-builder__message--${m.tone || 'note'}`}
					role={m.tone === 'error' ? 'alert' : undefined}
				>
					{m.tone === 'error' ? <EXCLAMATION_CIRCLE aria-hidden='true' className='chart-builder__message-icon' /> : null}
					<span>{m.text}</span>
				</div>
			))}
		</div>
	);
}

/**
 * An antd Select option that can explain itself: a disabled option carries
 * its reason as a muted second line, so "explain anything disabled" holds
 * without a hover (the options list is read aloud as written). A Select of
 * these sets `optionLabelProp='display'`, so a picked option the server then
 * refuses (an element that collides) shows its name in the box, and its
 * reason only under the field.
 */
export function explainedOptions(options = []) {
	return options.map((o) => ({
		value: o.value,
		// What the box shows once picked: the label alone, never the reason.
		display: o.label,
		disabled: Boolean(o.disabled),
		searchText: typeof o.label === 'string' ? o.label : String(o.value),
		title: o.reason || (typeof o.label === 'string' ? o.label : undefined),
		label: o.reason ? (
			<span className='chart-builder__option'>
				<span className='chart-builder__option-label'>{o.label}</span>
				<span className='chart-builder__option-why'>{o.reason}</span>
			</span>
		) : o.label,
	}));
}

/** The props a control takes from its messages: marked invalid, described by them. */
export function messageProps(messages, id) {
	const list = Array.isArray(messages) ? messages : [];
	const error = list.some((m) => m && m.tone === 'error');
	const props = {};
	if (list.some((m) => m && m.text)) props['aria-describedby'] = id;
	if (error) {
		props['aria-invalid'] = 'true';
		props.status = 'error';
	}
	return props;
}
